<template>
  <div class="work-space-custom-locale-control-container" @dblclick.stop v-show="shouldShowControl">
    <div class="custom-locale-control-btns">
      <el-button v-for="(btn, index) of buttonList" :key="index" :icon="getIcon(btn)" color="rgb(23, 44, 63)"
        :title="btn.name" @click="btn.onClick(btn)" :loading="btn[loadProp]"></el-button>
    </div>
    <div class="custom-locale-control-edit" v-show="editBodyShow">
      <el-scrollbar @wheel.stop @mousemove.stop max-height="25rem">
        <EditPopup v-show="editShowFlag[btn.name]" v-for="(btn, index) of buttonList" :key="index" :title="btn.name"
          :controls="btn.controls" :target="editTarget" :btn="btn">
          <component v-if="editShowFlag[btn.name] && btn.dom" :is="btn.dom"></component>
        </EditPopup>
      </el-scrollbar>
    </div>
  </div>

  <teleport to="#home-body">
    <ExposureEdit v-if="editTarget[exposureEditProp]" :data="exposureEditData" :layer="layer"
      @close="exposureEditClose"></ExposureEdit>
  </teleport>
</template>

<script setup>
import {
  onMounted, onUnmounted,
  computed, ref, nextTick, reactive, markRaw,
  h, defineComponent, toRaw,
} from 'vue';
import store from '@/store';
import { message } from '@/utils/js/initVariable';

import SvgIcon from '@/components/SvgIcon';
import EditPopup from './components/EditPopup';
import FeatureEdit from './components/FeatureEdit';
import EventEdit from './components/EventEdit';
import Frequency from './components/Frequency';
import RegionalShoot from './components/RegionalShoot';
import ExposureEdit from './components/ExposureEdit';
import RegionControl from './components/RegionControl';
import NameEdit from './components/NameEdit';
import IntentionEdit from './components/IntentionEdit';
import StyleEdit from './components/StyleEdit';
import TrajectoryClip from './components/TrajectoryClip';

// Composables
import { useOsmData } from './composables/useOsmData';
import { useAiAnalysis } from './composables/useAiAnalysis';
import { useFilmUpload } from './composables/useFilmUpload';
import { useClueArea } from './composables/useClueArea';
import { useSaveAndDelete } from './composables/useSaveAndDelete';

// props
const props = defineProps({
  target: { type: Object, default: () => { } },
  layer: { type: Object, required: true, },
  map: { type: Object, default: undefined, },
  layerGroup: { type: Object, },
  workSpace: { type: Object, },
  workSpaceDataList: { type: Array, },
  layerClick: { type: Function, default: () => { }, },
  onTrajectoryClipSucceed: { type: Function, default: () => { } },
});
// emit
const emit = defineEmits([
  'layerDelete', 'delPolyLayerSucceedFn',
]);


const homeMap = computed(() => store.getters.getHomeMap),
  map = props.map ? toRaw(props.map) : toRaw(homeMap.value);

const layerClickFn = () => {
  props.layer && props.layer.on({
    click: props.layerClick
  })
}

const filmLayerGroup = L.featureGroup().addTo(props.layerGroup),
  filmLayerGroupClear = () => {
    filmLayerGroup.clearLayers();
  },
  filmLayerGroupClose = () => {
    props.layerGroup.removeLayer(filmLayerGroup);
  };
const osmLayerGroup = L.featureGroup().addTo(props.layerGroup),
  osmLayerGroupClear = () => {
    osmLayerGroup.clearLayers();
  },
  osmLayerGroupClose = () => {
    props.layerGroup.removeLayer(osmLayerGroup);
  };
const editTarget = reactive({
  shootingLocationList: [],
  ...props.target,
  ...(props.layer['feature'] && props.layer['feature']['properties']),
})

const isWorkMode = computed(() => store.getters['isWorkMode']);

// 在首页且非工作模式时，不显示控件
const isHomePage = computed(() => store.getters['isHomePage']);
const shouldShowControl = computed(() => !isHomePage.value || isWorkMode.value);

const editBodyShow = computed(() => {
  for (const key in editShowFlag) {
    if (editShowFlag[key]) {
      return true;
    }
  }
  return false;
})

const loadProp = ref('loading');

const defaultFill = 'rgb(50, 184, 217)';
const getIcon = (item) => {
  const { icon, fill } = item;
  return !icon ? '' : defineComponent({
    setup() {
      return () => h(SvgIcon, {
        iconName: icon,
        fillColor: fill || defaultFill,
      })
    }
  })
}

const layerStyleAttrChange = (attr, val) => {
  const style = {};
  style[attr] = val;
  editTarget[attr] = val;
  props.layer.setStyle && props.layer.setStyle(style);
}

const editShowFlag = reactive({

});

const editShowFlagCatalogChange = (btn) => {
  editShowFlag[btn.name] = !editShowFlag[btn.name];
}

let featureScrollbarRef;
const featureAdd = (target, btn, control) => {
  target['featureList'].unshift({});
  featureScrollbarRef.setScrollTop && featureScrollbarRef.setScrollTop(0);
}

// ===== Composables 初始化 =====
const { osmRequest } = useOsmData(props, osmLayerGroup, osmLayerGroupClear, loadProp);
const { aiDataRequest, aiControllerClose } = useAiAnalysis(props, map, loadProp);
const { filmDataInit, filmDataRequest } = useFilmUpload(props, editTarget, filmLayerGroup, loadProp);
const { clueAdd } = useClueArea(props, loadProp);

const ifContinuousTrackingProp = 'ifContinuousTracking',
  ifContinuousStatisticsProp = 'ifContinuousStatistics';
const { saveSubmit, deleteFn, delPolyLayer, setFeatureChangeFn } = useSaveAndDelete(
  props, editTarget, emit, loadProp, ifContinuousTrackingProp, ifContinuousStatisticsProp
);


const isOffline = ref(__IS__OFFLINE__),
  isSuperAdmin = computed(() => store.getters['isSuperAdmin']);
const buttonList = computed(() => btnList.value.filter((e) => e));

const exposureEditProp = ref('exposureEditFlag'), exposureEditData = ref(undefined),
  exposureEditClose = () => {
    editTarget[exposureEditProp.value] = false;
    exposureEditData.value = undefined;
  }


const btnList = computed(() => [
  {
    name: '区域名称', icon: 'el-Edit', onClick: editShowFlagCatalogChange,
    controls: [
      { iconName: 'check-box', fill: 'rgb(102, 203, 118)', onClick: (target, btn) => { editShowFlagCatalogChange(btn) }, }
    ],
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(NameEdit, {
            modelValue: editTarget['annotationName'] || editTarget['regionName'],
            'onUpdate:modelValue': (val) => {
              editTarget['annotationName'] = val;
            },
          })
        }
      })
    ),
  },
  !isSuperAdmin.value ? undefined : {
    name: '区域特征', icon: '#', fill: 'rgb(238, 87, 64)', onClick: editShowFlagCatalogChange,
    controls: [
      { iconName: 'plus-box', fill: 'rgb(81, 177, 217)', onClick: featureAdd, }
    ],
    dom: markRaw(
      defineComponent({
        setup() {

          return () => h(FeatureEdit, {
            features: editTarget['regionFeatureList'] || editTarget['tags'],
            featureList: editTarget['featureList'] || editTarget['tags'],
            onFeatureInit: (list) => {
              editTarget['featureList'] = list;
            },
            onFeatureChangeFnInit: (fn) => {
              setFeatureChangeFn(fn);
            },
            onScrollbarRefChange: (scrollbarRef) => {
              featureScrollbarRef = scrollbarRef;
            }
          })
        }
      })
    ),
  },
  {
    name: '区域意图', icon: 'el-InfoFilled', onClick: editShowFlagCatalogChange,
    controls: [
      { iconName: 'check-box', fill: 'rgb(102, 203, 118)', onClick: (target, btn) => { editShowFlagCatalogChange(btn) }, }
    ],
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(IntentionEdit, {
            modelValue: editTarget['regionIntention'] || editTarget['regionDesc'],
            'onUpdate:modelValue': (val) => {
              editTarget['regionIntention'] = val;
            },
          })
        }
      })
    ),
  },
  reactive({
    name: '地理信息', icon: 'osm', onClick: osmRequest,
  }),
  !isWorkMode.value ? { name: '区域事件', icon: 'list-add', onClick: editShowFlagCatalogChange,
    controls: [],
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(EventEdit, {
            target: editTarget,
            layer: props.layer,
          })
        }
      })
    ),
  } : undefined,
  {
    name: '区域线索区频次', icon: 'chart', onClick: editShowFlagCatalogChange,
    controls: [
      reactive(isWorkMode.value ? {
        iconName: 'save', fill: 'rgb(102, 203, 118)', title: '保存',
        onClick: (target, btn, control) => {
          const data = target['requestFrequencyData'];
          if (!(data && data.length > 0)) {
            message('请查询线索区频次数据', 2);
            return;
          }
          exposureEditData.value = data;
          editTarget[exposureEditProp.value] = true;
        },
      } : {
        iconName: 'check-box', fill: 'rgb(102, 203, 118)',
        onClick: (target, btn) => {
          editTarget['exposureCountList'] = editTarget['requestFrequencyData'];
          editShowFlagCatalogChange(btn);
        },
      })
    ],
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(Frequency, {
            showFlag: editShowFlag['区域线索区频次'],
            layer: props.layer,
            data: props.target['exposureCountList'],
            onFrequencyDataChange: (dataList) => {
              editTarget['requestFrequencyData'] = dataList;
            }
          })
        }
      })
    )
  },
  (!isWorkMode.value && !isOffline.value) ? reactive({
    name: '区域摄像头', icon: 'film', onClick: filmDataRequest,
  }) : undefined,
  (!isWorkMode.value && !isOffline.value) ? reactive({
    name: 'AI解读', icon: 'ai', onClick: aiDataRequest,
  }) : undefined,
  (isWorkMode.value && !isOffline.value && isRectangle()) ? {
    name: '获取影像源', icon: 'image', onClick: editShowFlagCatalogChange,
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(RegionalShoot, {
            layer: props.layer,
            layerGroup: props.layerGroup,
          })
        }
      })
    ),
  } : undefined,
  (
    isWorkMode.value && !isOffline.value && isSuperAdmin.value
    && (props.layer['options']['layerType'] && props.layer['options']['layerType'] === 'polygon')
  ) ? reactive({
    name: '线索区识别', icon: 'region-add', onClick: clueAdd,
  }) : undefined,
  (
    isWorkMode.value && !isOffline.value
    && (props.layer['options']['layerType'] && props.layer['options']['layerType'] === 'polygon')
  ) ? reactive({
    name: '区域控制', icon: 'switch', onClick: editShowFlagCatalogChange,
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(RegionControl, {
            ifContinuousTracking: editTarget[ifContinuousTrackingProp],
            'onUpdate:ifContinuousTracking': (val) => {
              editTarget[ifContinuousTrackingProp] = val;
            },
            ifContinuousStatistics: editTarget[ifContinuousStatisticsProp],
            'onUpdate:ifContinuousStatistics': (val) => {
              editTarget[ifContinuousStatisticsProp] = val;
            },
          })
        }
      })
    ),
  }) : undefined,
  {
    name: '区域样式', icon: 'graphic-style', onClick: editShowFlagCatalogChange,
    controls: [
      { iconName: 'check-box', fill: 'rgb(102, 203, 118)', onClick: (target, btn) => { editShowFlagCatalogChange(btn) }, }
    ],
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(StyleEdit, {
            weight: editTarget['weight'],
            dashArray: editTarget['dashArray'],
            fill: editTarget['fill'],
            onStyleChange: (attr, val) => {
              layerStyleAttrChange(attr, val);
            },
          })
        }
      })
    ),
  },
  reactive({ name: '保存', icon: 'save', onClick: saveSubmit }),
  (!isWorkMode.value) ? reactive({ name: '删除', icon: 'delete', onClick: deleteFn }) : undefined,
  !isWorkMode.value ?
    reactive({ name: '删除区域内元素', icon: 'graphic-delete', onClick: delPolyLayer }) : undefined,
  !isWorkMode.value ? {
    name: '轨迹裁剪', icon: 'scissor', onClick: editShowFlagCatalogChange,
    dom: markRaw(
      defineComponent({
        setup() {
          return () => h(TrajectoryClip, {
            layer: props.layer,
            workSpaceDataList: props.workSpaceDataList,
            onSuccess: props.onTrajectoryClipSucceed,
            onClose: () => { editShowFlag['轨迹裁剪'] = false; },
          })
        }
      })
    ),
  } : undefined,
])

const isRectangle = () => {
  return props.layer.options['layerType'] === 'imageRec';
}

// 组件挂载后
onMounted(() => {
  nextTick(() => {
    filmDataInit(editTarget['shootingLocationList'])
    layerClickFn();
  })
});
// 组件销毁后
onUnmounted(() => {
  osmLayerGroupClose();
  filmLayerGroupClose();
  aiControllerClose();
});

</script>

<style lang='less'>
.work-space-custom-locale-control-container {
  .custom-locale-control-btns {
    .el-button+.el-button {
      margin-left: 0px;
    }

    .el-button {
      padding: 5px;
      height: auto;

      .el-icon {
        width: 14px;
        height: 14px;
      }
    }
  }
}

.popup-film-upload {
  .el-upload {
    flex-direction: column;
  }
}
</style>

<style lang='less' scoped>
.work-space-custom-locale-control-container {
  width: max-content;
  display: flex;
  gap: 5px;

  .custom-locale-control-btns {
    width: max-content;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .custom-locale-control-edit {

    // width: 200px;
    // height: 400px;
    :deep(.el-scrollbar) {
      .el-scrollbar__view {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }
    }
  }
}
</style>